import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import {
  faChevronRight,
  faEllipsis,
} from '@document-crunch/pro-regular-svg-icons'
import { cn } from '@dc-shared-web/lib'
import { Icon } from '@dc-shared-web/components/base/Icon'

const Breadcrumb = React.forwardRef<
  HTMLElement,
  React.ComponentPropsWithoutRef<'nav'> & {
    separator?: React.ReactNode
  }
>(({ ...props }, ref) => <nav ref={ref} aria-label='breadcrumb' {...props} />)
Breadcrumb.displayName = 'Breadcrumb'

const BreadcrumbList = React.forwardRef<
  HTMLOListElement,
  React.ComponentPropsWithoutRef<'ol'>
>(({ className, ...props }, ref) => (
  <ol
    ref={ref}
    className={cn(
      'tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-break-words tw-text-sm tw-text-muted-foreground sm:tw-gap-2.5',
      className
    )}
    {...props}
  />
))
BreadcrumbList.displayName = 'BreadcrumbList'

const BreadcrumbItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentPropsWithoutRef<'li'>
>(({ className, ...props }, ref) => (
  <li
    ref={ref}
    className={cn('tw-flex tw-items-center tw-gap-1.5 tw-truncate', className)}
    {...props}
  />
))
BreadcrumbItem.displayName = 'BreadcrumbItem'

const BreadcrumbLink = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentPropsWithoutRef<'a'> & {
    asChild?: boolean
    dataTestId?: string
  }
>(({ asChild, className, dataTestId, ...props }, ref) => {
  const Comp = asChild ? Slot : 'a'

  return (
    <Comp
      data-testid={`${dataTestId}-breadcrumb`}
      ref={ref}
      className={cn(
        'tw-font-medium tw-text-neutral-400 tw-inline-block hover:tw-text-neutral-950',
        className
      )}
      {...props}
    />
  )
})
BreadcrumbLink.displayName = 'BreadcrumbLink'

const BreadcrumbPage = React.forwardRef<
  HTMLSpanElement,
  React.ComponentPropsWithoutRef<'span'>
>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    role='link'
    aria-disabled='true'
    aria-current='page'
    className={cn('tw-font-medium tw-text-neutral-950', className)}
    {...props}
  />
))
BreadcrumbPage.displayName = 'BreadcrumbPage'

const BreadcrumbSeparator = ({
  children,
  className,
  ...props
}: React.ComponentProps<'li'>) => (
  <li
    role='presentation'
    aria-hidden='true'
    className={cn(
      'tw-[&>svg]:tw-w-3.5 tw-[&>svg]:tw-h-3.5 tw-flex-shrink-0',
      className
    )}
    {...props}
  >
    {children ?? (
      <Icon icon={faChevronRight} className='tw-text-neutral-400' size='sm' />
    )}
  </li>
)
BreadcrumbSeparator.displayName = 'BreadcrumbSeparator'

const BreadcrumbEllipsis = ({
  className,
  ...props
}: React.ComponentProps<'span'>) => (
  <span
    role='presentation'
    aria-hidden='true'
    className={cn(
      'tw-flex tw-items-center tw-justify-center tw-flex-shrink-0',
      className
    )}
    {...props}
  >
    <Icon icon={faEllipsis} className='tw-text-neutral-400' size='sm' />
    <span className='tw-sr-only'>More</span>
  </span>
)
BreadcrumbEllipsis.displayName = 'BreadcrumbElipssis'

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
}
